/* eslint-disable react-refresh/only-export-components */
import Cookies from 'js-cookie'
import type React from 'react'
import { createContext, useContext, useEffect, useState } from 'react'
import type {
  CookieConsentContextType,
  CookieConsentProviderProps,
  CookiePreferences,
} from '@/types/contexts'

declare global {
  interface Window {
    gtag?: (command: string, action: string, params?: object) => void
  }
}

export type { CookiePreferences }

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined)

const getCookie = (name: string): string | null => {
  return Cookies.get(name) || null
}

const setCookie = (name: string, value: string, days: number = 90) => {
  Cookies.set(name, value, {
    expires: days,
    path: '/',
    secure: window.location.protocol === 'https:',
    sameSite: 'strict',
  })
}

export const CookieConsentProvider: React.FC<CookieConsentProviderProps> = ({ children }) => {
  const [hasConsent, setHasConsent] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [showBanner, setShowBanner] = useState<boolean>(false)
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
    functional: false,
  })

  useEffect(() => {
    const cookieConsent = getCookie('cookieConsent')
    if (cookieConsent) {
      try {
        const savedPreferences = JSON.parse(decodeURIComponent(cookieConsent))
        setPreferences(savedPreferences)
        setHasConsent(true)
        setShowBanner(false)
      } catch (_error) {
        setShowBanner(true)
      }
    } else {
      setShowBanner(true)
    }
    setIsLoading(false)
  }, [])

  const updatePreferences = (newPreferences: CookiePreferences) => {
    setPreferences(newPreferences)

    // Save preferences regardless of choice to ensure the banner is hidden
    setCookie(
      'cookieConsent',
      encodeURIComponent(JSON.stringify(newPreferences)),
      90, // 3 months
    )
    setHasConsent(true)
    setShowBanner(false)

    // Trigger Google Analytics or other tracking based on preferences
    if (newPreferences.analytics) {
      // Enable analytics tracking
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('consent', 'update', {
          analytics_storage: 'granted',
        })
      }
    } else if (typeof window !== 'undefined' && window.gtag) {
      // Disable analytics tracking
      window.gtag('consent', 'update', {
        analytics_storage: 'denied',
      })
    }
  }

  const clearConsent = () => {
    Cookies.remove('cookieConsent', { path: '/' })
    setHasConsent(false)
    setShowBanner(true)
    setPreferences({
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false,
    })
  }

  const openPreferences = () => {
    setShowBanner(true)
  }

  return (
    <CookieConsentContext.Provider
      value={{
        hasConsent,
        isLoading,
        showBanner,
        preferences,
        updatePreferences,
        clearConsent,
        openPreferences,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  )
}

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext)
  if (context === undefined) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider')
  }
  return context
}
